import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, FormHelperText, Grid, Typography } from "@mui/material";
import React from "react";
import { DateField } from "../../../components/FormComponents/DateField";
import { FileUploadField } from "../../../components/FormComponents/FileUploadField";
import { NumericInput } from "../../../components/FormComponents/NumericInput";
import { ITheme } from "../../../utils/theme";
import { IEmployeeInfo, IPaidMedicalLeaveRequestData } from "../PaidMedicalLeaveRequestData";
import { defaultDisclaimer, IDisclaimer, MAX_ATTACHMENTS } from "../PaidMedicalLeaveRequestModels";
import { Disclaimer } from "./Disclaimer";

interface IDaysRequestedProps {
  data: IPaidMedicalLeaveRequestData;
  disclaimer: IDisclaimer;
  onShowDisclaimer: (disclaimer: IDisclaimer) => void;
  error: Record<string, JSX.Element | string>;
  onDateChange: (field: keyof IEmployeeInfo, value: string) => void;
  onDateBlur: (field: keyof IEmployeeInfo) => void;
  onFileChange: (files: FileList | []) => void;
  onRemoveFile: (index: number) => void;
  blurredFields: Partial<Record<keyof IEmployeeInfo, boolean>>
}

export const DaysRequested: React.FC<IDaysRequestedProps> = ({
  data,
  disclaimer,
  onShowDisclaimer,
  error,
  onDateChange,
  onDateBlur,
  onFileChange,
  onRemoveFile,
  blurredFields
}) => (
  <Box sx={{ p: { xs: 0, md: 2 } }}>
    {disclaimer && (!!disclaimer?.title || !!disclaimer.text?.length) && (
      <Disclaimer
        onClick={() => onShowDisclaimer(defaultDisclaimer)}
        title={disclaimer?.title || ""}
        text={disclaimer?.text || []}
        bulletParagraph={disclaimer.bulletParagraph}
      />
    )}
    <Grid container spacing={2}>
      <Grid item xs={6} sx={{ p: 0 }}>
        <Box
          sx={{ display: "flex", cursor: "pointer" }}
          onClick={() =>
            onShowDisclaimer({
              title: "Situations Covered",
              text: [
                "Personal illness or inquiry",
                "OOrgan or tissues donation from the employee",
                "Medical appointments for the employee during working hours",
                "Quarantine of the employee"
              ],
              bulletParagraph: true
            })
          }
        >
          <HelpOutlineIcon sx={{ color: "corporateBlue", mr: 1 }} />
          <Typography sx={{ color: "corporateBlue", textDecoration: 'underline' }}>Situation Covered</Typography>
        </Box>
      </Grid>
      <Grid item xs={6} sx={{ p: 0 }}>
        <Box
          sx={{ display: "flex", cursor: "pointer" }}
          onClick={() =>
            onShowDisclaimer({
              title: "Additional Information",
              text: [
                "May be taken in one or more periods",
                "Periods must be at least one (1) day",
                "For absences of five (5) days or longer, the employee will be required to provide a medical certificate issued by a health care practitioner"
              ],
              bulletParagraph: true
            })
          }
        >
          <HelpOutlineIcon sx={{ color: "corporateBlue", mr: 1 }} />
          <Typography sx={{ color: "corporateBlue", textDecoration: 'underline' }}>Additional Information</Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="form-label">
          Requested Period Of Leave
          <Typography component={"span"} sx={{ color: (theme: ITheme) => theme.palette.red.A700 }}>
            &nbsp;*
          </Typography>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <DateField
          label="From"
          value={data?.employeeInfo.requestedDateFrom || ""}
          onChange={(value) => onDateChange("requestedDateFrom", value)}
          onBlur={() => onDateBlur("requestedDateFrom")}
          required
          error={error?.requestedDateFrom || error.requestedDateTo}
        />
      </Grid>
      <Grid item xs={12}>
        <DateField
          label="To"
          value={data?.employeeInfo.requestedDateTo || ""}
          onChange={(value) => onDateChange("requestedDateTo", value)}
          onBlur={() => onDateBlur("requestedDateTo")}
          required
          error={error?.requestedDateTo || error.requestedDateFrom}
        />
      </Grid>
      {
        (error?.requestedDateTo || error.requestedDateFrom) && <Grid item xs={12}>
          <FormHelperText
            component={'div'}
            error={!!error}
          >
            {error?.requestedDateTo || error.requestedDateFrom}
          </FormHelperText>
        </Grid>
      }
      <Grid item xs={12}>
        <NumericInput
          label="Number of Days"
          value={data?.employeeInfo.numberOfDaysRequested ?? null}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex' }}>
          <HelpOutlineIcon sx={{ color: "corporateBlue", mr: 1 }} />
          <Box
            sx={{ display: "flex", cursor: "pointer" }}
            onClick={() =>
              onShowDisclaimer({
                title: "Notice Requirements",
                text: [
                  'Applications must be submitted at least 4 weeks in advance of a medical leave (If there is a valid reason why this cannot be done, the application must be provided as soon as possible)',
                  'Applications not submitted within 1 week following the return to work of the medical leave may be denied',
                  'Employees must notify their employer (where applicable) and WEBC, as soon as possible, if there is a change to the length of the leave'
                ],
                bulletParagraph: true
              })
            }
          >
            <Typography sx={{ color: "corporateBlue", textDecoration: 'underline' }}>Notice Requirements</Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{ display: "flex", cursor: "pointer" }}
          onClick={() =>
            onShowDisclaimer({
              title: "Supporting Documents",
              text: [
                <Typography sx={{ mb: 2 }}>
                  <Typography component='span' sx={{ fontWeight: 'bold', display: 'inline-block', mr: 1 }}>For any leave request of five (5) days or longer:</Typography>
                  Please attach a certificate signed by a health care practitioner to this application within 15 days of return to work.  A Health care practitioner means a person lawfully entitled, under the laws of a province, to provide health services in the place in which they provide those services.
                </Typography>,
                <Typography sx={{ pb: 0 }}>Dispatch Eligibility:</Typography>,
                'If outside Vancouver (Local 500), please have your union representative confirm that you would have received work on the day and shift for which Medical Leave is sought.'
              ],
            })
          }
        >
          <Typography sx={{ color: "corporateBlue" }}>Upload Supporting Documents:</Typography>
          <HelpOutlineIcon sx={{ color: "corporateBlue", mr: 1 }} />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ color: (theme: ITheme) => theme.palette.corporateBlue }}>Notes:</Typography>
          <Typography sx={{ color: (theme: ITheme) => theme.palette.corporateBlue }}>1. Each file size must not exceed 1 MB.</Typography>
          <Typography sx={{ color: (theme: ITheme) => theme.palette.corporateBlue }}>2. A minimum of one supporting document is required for Paid Medical Leaves of more than 5 requested days.</Typography>
        </Box>
        <FileUploadField
          disabled={data?.employeeInfo?.attachments.length >= MAX_ATTACHMENTS}
          onRemoveFile={onRemoveFile}
          onFileChange={(files) => onFileChange(files)}
          files={data?.employeeInfo.attachments || []}
          required={data?.employeeInfo?.numberOfDaysRequested >= MAX_ATTACHMENTS}
          error={blurredFields.attachments ? error?.attachments : ''}
        />
      </Grid>
    </Grid>
  </Box>
);
